/* eslint-disable max-len */
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import MixpanelPageViewStore from '@/store/mixpanel/pageView';
import UtmParamsStore from '@/store/utm/utm';

import GuestLayout from '@/components/layout/GuestLayout.vue';
import NoHeaderLayout from '@/components/layout/NoHeaderLayout.vue';
import HomeView from '@/views/HomeView.vue';
import AuthStore from '@/store/auth';
import sessionRoutes from '@/router/session';
import userRoutes from '@/router/user';
import merchantRoutes from '@/router/merchant';
import dashboardRoutes from '@/router/dashboard';
import liveRoutes from '@/router/stream';
import pluginsRoutes from '@/router/plugins';
import billingRoutes from '@/router/billing';
import affiliateRoutes from '@/router/affiliate';
import crmRoutes from '@/router/crm';
import automacoesRoutes from '@/router/automacoes';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'HomePage',
    component: HomeView,
    meta: {
      layout: GuestLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
  },
  {
    path: '/@:username',
    name: 'BioPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "bioPublic" */ '@/views/tailwind/bio/BioPublicView.vue'),
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditionsPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "main" */ '@/views/tailwind/documents/TermsAndConditionsView.vue'),
  },
  {
    path: '/terms-and-conditions/shoppers',
    name: 'TermsAndConditionsPageShoppers',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "main" */ '@/views/tailwind/documents/TermsAndConditionsShoppersView.vue'),
  },
  {
    path: '/privacy',
    name: 'PrivacyPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "main" */ '@/views/tailwind/documents/PrivacyView.vue'),
  },
  // merge routes
  ...sessionRoutes,
  ...dashboardRoutes,
  ...userRoutes,
  ...merchantRoutes,
  ...liveRoutes,
  ...pluginsRoutes,
  ...billingRoutes,
  ...affiliateRoutes,
  ...crmRoutes,
  ...automacoesRoutes,
  // Public plugin bio page — `/handle` (no `@`).
  // Regex restricts to Instagram-like handles so this doesn't shadow accidental
  // unhandled paths. Static routes (/dashboard, /bio, /plugins, etc.) win
  // automatically due to Vue Router's static-over-dynamic precedence.
  {
    path: '/:handle([a-zA-Z0-9_][a-zA-Z0-9_.]*)',
    name: 'PluginBioPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "bioPublic" */ '@/views/tailwind/plugins/PluginBioPublicView.vue'),
  },
  {
    path: '/oops-404', // matches any path
    name: 'NotFoundPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "error" */ '@/views/tailwind/errors/404View.vue'),
  },
  {
    path: '/error', // matches any path
    name: 'ErrorPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "error" */ '@/views/tailwind/errors/ErrorView.vue'),
  },
  {
    path: '/oops-500', // matches any path
    name: 'InternalServerErrorPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "error" */ '@/views/tailwind/errors/ErrorView.vue'),
  },
  {
    path: '/:catchAll(.*)', // matches any path
    // name: 'NotFoundPage',
    meta: {
      layout: NoHeaderLayout,
      accessWithSession: undefined,
      useTailwind: true,
    },
    component: () => import(/* webpackChunkName: "error" */ '@/views/tailwind/errors/404View.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,

  scrollBehavior(to, from, savedPosition) {
    // If the route has a hash (#), scroll to the element with that ID
    if (to.hash) {
      return { el: to.hash };
    }
    // If the route has a saved scroll position (back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }
    // Same path, only query/state changed — keep the current scroll. This
    // matters for views that drive their filters through the URL (e.g. the
    // CRM tab on the plugin dashboard): tweaking a filter mid-page on
    // mobile was jumping the user back to the top.
    if (to.path === from.path) {
      return false;
    }
    // Different path → scroll to the top of the page
    return { top: 0, left: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const bulma = document.getElementById('bulma-styles') as HTMLLinkElement;
  const tailwind = document.getElementById('tailwind-styles') as HTMLLinkElement;

  // Explicitly enable/disable both on every route
  bulma.disabled = !!to.meta.useTailwind;
  tailwind.disabled = !to.meta.useTailwind;

  // Setting UTM parameters on storage
  const { setUTMParameters } = UtmParamsStore();

  // Always set UTM parameters if present in the URL
  if (to.query.utm_source || to.query.utm_medium || to.query.utm_campaign || to.query.utm_term || to.query.utm_content) {
    setUTMParameters();
  }

  const { isAuthenticated, mustChangePassword } = AuthStore();

  // User is trying to access a page that does not require auth
  if (to.meta.accessWithSession === undefined) {
    // alert('User is trying to access a page that does not care about auth');
    next();
    return;
  }

  // User is unauthenticated and trying to access a page that requires authentication
  if (to.meta.accessWithSession === true && !isAuthenticated()) {
    // alert('User is unauthenticated and trying to access a page that requires authentication');
    // localStorage.setItem('tempMessage', to.fullPath);
    next({
      name: 'SessionLoginPage',
      query: {
        redirect: to.fullPath,
      },
    });
    return;
  }

  // User is authenticated and trying to access a page that does not accept authenticated users
  if (to.meta.accessWithSession === false && isAuthenticated()) {
    // alert('User is authenticated and trying to access a page that does not accept authenticated users');
    const previousUrl = from.fullPath || '/dashboard';
    next(previousUrl);
    return;
  }

  // User is logging in and must change password
  if (
    to.name !== 'SessionPasswordResetPage'
    && to.name !== 'SessionLoginRefreshPage'
    && mustChangePassword()
  ) {
    next({
      name: 'SessionPasswordResetPage',
      query: {
        redirect: to.fullPath,
      },
    });
    return;
  }

  // Default case
  next();
});

router.afterEach((to, from) => {
  new MixpanelPageViewStore().trackPageView(
    from.name,
    to.name,
    to.path,
  );
});

// Stale-chunk recovery. Every non-eager route is `() => import(...)` and
// gets a content-hashed chunk filename. After a redeploy those hashes
// change; a browser tab left open holds a stale module graph that points
// at chunks the CDN no longer has. Instead of a blank screen on the next
// navigation, force a hard reload of the intended URL so the browser
// picks up the fresh index.html + asset manifest. sessionStorage gates
// the reload to once per URL — a genuinely broken deploy won't
// reload-loop forever.
function isChunkLoadFailure(err: unknown): boolean {
  if (!err) return false;
  const msg = String((err as Error)?.message || err);
  return (
    /Loading chunk .+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /The string did not match the expected pattern/i.test(msg)
  );
}

router.onError((err, to) => {
  if (!isChunkLoadFailure(err)) return;
  const target = (to && to.fullPath) || window.location.pathname;
  const key = `chunkReloaded:${target}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  window.location.replace(target);
});

export default router;
